import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr, API_BASE, getStoredToken } from "@/lib/api";
import { FileText, Loader2, Copy, ExternalLink, Download, RefreshCw, Lock, CheckCircle2, MessageSquare, SlidersHorizontal, List, AlignLeft } from "lucide-react";

export default function ProposalTab({ projectId }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [usePw, setUsePw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState("full");
  const [terms, setTerms] = useState("");
  const [validity, setValidity] = useState(30);
  const [clientEmail, setClientEmail] = useState("");
  const [localRatesNote, setLocalRatesNote] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["proposal", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/proposal`)).data });
  const { data: project } = useQuery({ queryKey: ["project", projectId], queryFn: async () => (await api.get(`/projects/${projectId}`)).data });

  useEffect(() => {
    if (!project) return;
    setDetail(project.proposal_detail || "full");
    setValidity(project.proposal_validity_days || 30);
    setClientEmail(project.client_email || "");
    setLocalRatesNote(project.proposal_local_rates !== false);
    if (Array.isArray(project.proposal_terms)) setTerms(project.proposal_terms.join("\n"));
  }, [project]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/proposal`, { password: usePw && password ? password : null });
      qc.invalidateQueries({ queryKey: ["proposal", projectId] });
      toast.success("Share link ready");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const termsArr = terms.split("\n").map((t) => t.trim()).filter(Boolean);
      await api.patch(`/projects/${projectId}`, { proposal_detail: detail, proposal_validity_days: Number(validity) || 30, proposal_terms: termsArr, client_email: clientEmail.trim(), proposal_local_rates: localRatesNote });
      await qc.invalidateQueries({ queryKey: ["project", projectId] });
      if (data?.exists) await create();
      toast.success("Proposal settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingSettings(false); }
  };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const shareUrl = data?.exists ? `${window.location.origin}/proposal/${data.token}` : "";
  const pdfUrl = `${API_BASE}/projects/${projectId}/proposal/pdf?auth=${encodeURIComponent(getStoredToken())}&detail=${detail}`;

  const settingsCard = (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="proposal-settings">
      <h3 className="font-semibold mb-3 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-primary" /> Proposal Settings</h3>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Line-item detail</div>
      <div className="flex rounded-lg border border-border overflow-hidden text-sm mb-4 w-fit" data-testid="detail-toggle">
        <button data-testid="detail-full" onClick={() => setDetail("full")} className={`px-3 py-1.5 font-semibold inline-flex items-center gap-1.5 ${detail === "full" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}><List className="h-3.5 w-3.5" />Full breakdown</button>
        <button data-testid="detail-summary" onClick={() => setDetail("summary")} className={`px-3 py-1.5 font-semibold inline-flex items-center gap-1.5 ${detail === "summary" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}><AlignLeft className="h-3.5 w-3.5" />Summary only</button>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Validity (days)</div>
      <input data-testid="validity-input" type="number" value={validity} onChange={(e) => setValidity(e.target.value)} className="w-28 rounded-lg border border-input bg-popover px-3 py-2 text-sm font-mono outline-none focus:border-primary mb-4" />
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Client email <span className="normal-case">(change-order notifications)</span></div>
      <input data-testid="client-email-input" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="ops@acme.com"
        className="w-full rounded-lg border border-input bg-popover px-3 py-2 text-sm outline-none focus:border-primary mb-4" />
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Payment terms & conditions (one per line)</div>
      <textarea data-testid="terms-input" value={terms} onChange={(e) => setTerms(e.target.value)} rows={5}
        placeholder={"Payment terms: 30% mobilisation, progress billing monthly, 10% retention.\nPricing valid for the period above.\nChanges may require a formal change order."}
        className="w-full rounded-lg border border-input bg-popover px-3 py-2.5 text-sm outline-none focus:border-primary resize-none mb-3" />
      <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer">
        <input type="checkbox" checked={localRatesNote} onChange={(e) => setLocalRatesNote(e.target.checked)} data-testid="local-rates-note-toggle" className="mt-0.5" />
        <span>Show <span className="font-semibold text-emerald-500">"Local contractor rates applied"</span> note on the PDF <span className="text-muted-foreground">(when the estimate is priced from live local market data)</span></span>
      </label>
      <button data-testid="save-settings-btn" onClick={saveSettings} disabled={savingSettings}
        className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60 text-sm">
        {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save {data?.exists ? "& refresh" : "settings"}
      </button>
    </div>
  );

  if (!data?.exists) {
    return (
      <div className="max-w-lg mx-auto space-y-5">
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <FileText className="h-10 w-10 mx-auto text-primary mb-3" />
        <h3 className="font-semibold text-lg">Create a client proposal</h3>
        <p className="text-sm text-muted-foreground mb-5">Snapshot the current estimate into a branded PDF with a secure link your client can view, comment on and accept online.</p>
        <label className="flex items-center gap-2 justify-center text-sm mb-3 cursor-pointer">
          <input type="checkbox" checked={usePw} onChange={(e) => setUsePw(e.target.checked)} data-testid="proposal-pw-toggle" /> Password-protect the link
        </label>
        {usePw && <input data-testid="proposal-pw-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Set a password" className="w-full rounded-lg border border-input bg-popover px-3 py-2.5 mb-4 outline-none focus:border-primary" />}
        <button data-testid="create-proposal-btn" onClick={create} disabled={busy} className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate proposal
        </button>
      </div>
      {settingsCard}
      </div>
    );
  }

  const statusCls = { sent: "text-sky-400", viewed: "text-amber-400", accepted: "text-emerald-500" }[data.status] || "text-muted-foreground";

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="proposal-link-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Client Proposal</h3>
            <span className={`text-xs font-mono uppercase tracking-wider ${statusCls}`}>{data.status}</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Secure share link</div>
          <div className="flex gap-2 mb-4">
            <input readOnly value={shareUrl} data-testid="share-url" className="flex-1 rounded-lg border border-input bg-popover px-3 py-2 text-sm font-mono outline-none" />
            <button data-testid="copy-link-btn" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }} className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-secondary"><Copy className="h-4 w-4" /></button>
            <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="open-link-btn" className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-secondary"><ExternalLink className="h-4 w-4" /></a>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-pdf-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors"><Download className="h-4 w-4" />Download PDF</a>
            <button data-testid="refresh-proposal-btn" onClick={create} disabled={busy} className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 font-semibold hover:bg-secondary transition-colors"><RefreshCw className="h-4 w-4" />Refresh snapshot</button>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5" data-testid="proposal-comments">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Client comments</h3>
          {(data.comments || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No comments yet.</p>
          ) : (
            <div className="space-y-3">
              {data.comments.map((c, i) => (
                <div key={i} className="border-l-2 border-primary/40 pl-3">
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-sm text-muted-foreground">{c.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div className={`rounded-xl border p-5 ${data.status === "accepted" ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card"}`} data-testid="proposal-status-card">
          <h3 className="font-semibold mb-3">Status</h3>
          {data.status === "accepted" ? (
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-500">Accepted</div>
                <div className="text-sm text-muted-foreground">by {data.accepted_by}</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Awaiting client action. Share the link above.</p>
          )}
          {data.has_password && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3"><Lock className="h-3.5 w-3.5" />Password protected</div>}
        </div>
        {settingsCard}
      </div>
    </div>
  );
}
